import { useState } from "react";
import { useApplyAttemptRealtime } from "@/hooks/useApplyAttemptRealtime";
import { ManualFallbackDialog } from "@/components/ManualFallbackDialog";
import { supabase } from "@/integrations/supabase/client";
import { useApplications } from "@/hooks/useApplications";

interface FallbackState {
  open: boolean;
  jobId: string | null;
  applyUrl: string | null;
  errorReason: string | null;
  jobTitle: string | null;
  companyName: string | null;
  payloadSnapshot: Record<string, unknown> | null;
}

/**
 * Provider component that sets up real-time notifications for apply attempts.
 * Include this once at the app level to enable real-time status updates.
 * Also handles zero-friction manual fallback dialog.
 */
export function ApplyAttemptRealtimeProvider() {
  const { markAsApplied } = useApplications();
  const [fallback, setFallback] = useState<FallbackState>({
    open: false,
    jobId: null,
    applyUrl: null,
    errorReason: null,
    jobTitle: null,
    companyName: null,
    payloadSnapshot: null,
  });

  // Set up the real-time subscription with manual fallback handler
  useApplyAttemptRealtime({
    onManualFallback: async (attemptId, jobId, applyUrl, errorReason) => {
      // Hämta jobb-info och payload snapshot för dialogen
      try {
        const [jobResult, attemptResult] = await Promise.all([
          supabase
            .from("aggregated_jobs")
            .select("title, company")
            .eq("id", jobId)
            .maybeSingle(),
          supabase
            .from("apply_attempts")
            .select("payload_snapshot")
            .eq("id", attemptId)
            .maybeSingle(),
        ]);
        
        const payloadSnapshot = attemptResult.data?.payload_snapshot 
          ? (attemptResult.data.payload_snapshot as Record<string, unknown>)
          : null;
        
        setFallback({
          open: true,
          jobId,
          applyUrl,
          errorReason,
          jobTitle: jobResult.data?.title || null,
          companyName: jobResult.data?.company || null,
          payloadSnapshot,
        });
      } catch {
        // Fallback om info inte kan hämtas
        setFallback({
          open: true,
          jobId,
          applyUrl,
          errorReason,
          jobTitle: null,
          companyName: null,
          payloadSnapshot: null,
        });
      }
    },
  });
  
  return (
    <>
      {/* Manual fallback dialog */}
      <ManualFallbackDialog
        open={fallback.open}
        onOpenChange={(open) => setFallback((prev) => ({ ...prev, open }))}
        jobId={fallback.jobId}
        jobTitle={fallback.jobTitle || undefined}
        companyName={fallback.companyName || undefined}
        applyUrl={fallback.applyUrl}
        errorReason={fallback.errorReason || undefined}
        payloadSnapshot={fallback.payloadSnapshot}
        onMarkAsApplied={markAsApplied}
      />
    </>
  );
}
